// This module is initially copied from finnish-ssn package:
// npmjs.com: https://www.npmjs.com/package/finnish-ssn
// GitHub: https://github.com/vkomulai/finnish-ssn/blob/47668a4376b3995bf1a33246f55de34a1a600e59/src/finnish-ssn.ts
// MIT License: https://github.com/vkomulai/finnish-ssn/blob/47668a4376b3995bf1a33246f55de34a1a600e59/LICENSE
//
// We could not use origianl package because our requirement was that SSNs are generated from "temp test series 900",
// but now we have modified also other things to meet our needs. Because of that switching over to original package
// is not option anymore even they add support for "temp test series 900".

'use strict'

import { assertValue } from '../../helpers'

enum Sex {
  FEMALE = 'female',
  MALE = 'male',
}

interface SSN {
  valid: boolean
  sex: Sex
  ageInYears: number
  dateOfBirth: Date
}

export class FinnishSSN {
  /**
   * Parse parameter given SSN string into Object representation.
   * @param ssn - {String} SSN to parse
   */
  public static parse(ssn: string): SSN {
    //  Sanity and format check, which allows to make safe assumptions on the format.
    if (!SSN_REGEX.test(ssn)) {
      throw new Error('Not valid SSN format')
    }

    const dayOfMonth = parseInt(ssn.substring(0, 2), 10)
    const month = ssn.substring(2, 4)
    const centuryId = ssn.charAt(6)
    const century = centuryMap.get(centuryId)
    if (!century) {
      throw new Error('Not valid century')
    }
    const year = parseInt(ssn.substring(4, 6), 10) + century
    const rollingId = ssn.substring(7, 10)
    const checksum = ssn.substring(10, 11)
    const sex = parseInt(rollingId, 10) % 2 ? Sex.MALE : Sex.FEMALE
    const daysInMonth = daysInGivenMonth(year, month)
    if (!daysInMonth) {
      throw new Error('Not valid days in month')
    }

    if (!daysInMonthMap.get(month) || dayOfMonth > daysInMonth) {
      throw new Error('Not valid SSN')
    }

    const checksumBase = parseInt(ssn.substring(0, 6) + rollingId, 10)
    const dateOfBirth = new Date(year, parseInt(month, 10) - 1, dayOfMonth, 0, 0, 0, 0)
    const today = new Date()

    return {
      valid: checksum === checksumTable[checksumBase % 31],
      sex,
      dateOfBirth,
      ageInYears: ageInYears(dateOfBirth, today),
    }
  }

  /**
   * Validates parameter given SSN. Returns true if SSN is valid, otherwise false.
   * @param ssn - {String} For example 'xxxxxx-xxxx'
   */
  public static validate(ssn: string): boolean {
    try {
      return FinnishSSN.parse(ssn).valid
    } catch (_error) {
      return false
    }
  }

  /**
   * Creates a valid SSN using the given age (Integer). Creates randomly male and female SSN'n.
   * In case an invalid age is given, throws exception.
   *
   * @param age as Integer. Min valid age is 1, max valid age is 200
   */
  public static createWithAge(age: number, hasBirthdayToday = false): string {
    if (age < MIN_AGE || age > MAX_AGE) {
      throw new Error(`Given age (${age}) is not between sensible age range of ${MIN_AGE} and ${MAX_AGE}`)
    }
    const today = new Date()
    let year = today.getFullYear() - age
    const month = hasBirthdayToday ? getCurrentMonth(today) : randomMonth()
    let dayOfMonth = hasBirthdayToday ? getCurrentDate(today) : randomDay(year, month)
    const rollingId = randomNumber(100) + 899 //  changed here

    if (!birthDayPassed(new Date(year, Number(month) - 1, Number(dayOfMonth)), today)) {
      if (FinnishSSN.isLeapYear(year)) {
        if (dayOfMonth === '29' && month === february) {
          dayOfMonth = '28'
        }
      }
      year--
    }

    const possibleCenturySigns: string[] = []
    centuryMap.forEach((value: number, key: string) => {
      if (value === Math.floor(year / 100) * 100) {
        possibleCenturySigns.push(key)
      }
    })
    const centurySign = possibleCenturySigns[Math.floor(Math.random() * possibleCenturySigns.length)]

    year = year % 100
    const yearString = yearToPaddedString(year)
    const checksumBase = parseInt(dayOfMonth + month + yearString + rollingId, 10)
    const checksum = checksumTable[checksumBase % 31]

    return dayOfMonth + month + yearString + centurySign + rollingId + checksum
  }

  public static isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  }

  public static isValidSsn(candidate?: string): boolean {
    if (!candidate) {
      return false
    }
    const SSN_LENGTH = 11
    const ALLOWED_SSN_SEPARATOR_CHARACTERS = '-ABCDEFYXWVU+'
    const ssn = candidate.toUpperCase()
    const calculateCheckCode = (num: number): string | null => {
      const chars = '0123456789ABCDEFHJKLMNPRSTUVWXY'
      return chars[num] ?? null
    }
    if (ssn.length !== SSN_LENGTH) {
      return false
    }
    if (!ALLOWED_SSN_SEPARATOR_CHARACTERS.includes(assertValue(ssn[6], 'ssn[6]'))) {
      return false
    }
    const checkCode = calculateCheckCode(
      parseInt(
        assertValue(ssn[0], 'ssn[0]') + ssn[1] + ssn[2] + ssn[3] + ssn[4] + ssn[5] + ssn[7] + ssn[8] + ssn[9],
        10,
      ) % 31,
    )
    if (ssn[10] !== checkCode) {
      return false
    }
    return true
  }
}

// deepcode ignore CollectionUpdatedButNeverQueried/test: <false positive>
const centuryMap: Map<string, number> = new Map()
centuryMap.set('F', 2000)
centuryMap.set('E', 2000)
centuryMap.set('D', 2000)
centuryMap.set('C', 2000)
centuryMap.set('B', 2000)
centuryMap.set('A', 2000)
centuryMap.set('U', 1900)
centuryMap.set('V', 1900)
centuryMap.set('W', 1900)
centuryMap.set('X', 1900)
centuryMap.set('Y', 1900)
centuryMap.set('-', 1900)
centuryMap.set('+', 1800)

const february = '02'
const daysInMonthMap: Map<string, number> = new Map()
daysInMonthMap.set('01', 31)
daysInMonthMap.set('02', 28)
daysInMonthMap.set('03', 31)
daysInMonthMap.set('04', 30)
daysInMonthMap.set('05', 31)
daysInMonthMap.set('06', 30)
daysInMonthMap.set('07', 31)
daysInMonthMap.set('08', 31)
daysInMonthMap.set('09', 30)
daysInMonthMap.set('10', 31)
daysInMonthMap.set('11', 30)
daysInMonthMap.set('12', 31)

const checksumTable: string[] = '0123456789ABCDEFHJKLMNPRSTUVWXY'.split('')

const MIN_AGE = 1
const MAX_AGE = 200
const SSN_REGEX = /^(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])([5-9]\d\+|\d\d[-U-Y]|[012]\d[A-F])\d{3}[\dA-Z]$/

function randomMonth(): string {
  return `0${randomNumber(12)}`.slice(-2)
}

function yearToPaddedString(year: number): string {
  return year % 100 < 10 ? `0${year}` : year.toString()
}

function randomDay(year: number, month: string): string {
  const maxDaysInMonth = daysInGivenMonth(year, month)
  if (!maxDaysInMonth) {
    throw new Error('Not valid max days in month')
  }

  return `0${randomNumber(maxDaysInMonth)}`.slice(-2)
}

function daysInGivenMonth(year: number, month: string) {
  const daysInMonth = daysInMonthMap.get(month)
  if (!daysInMonth) {
    throw new Error('Not valid days in given month')
  }

  return month === february && FinnishSSN.isLeapYear(year) ? daysInMonth + 1 : daysInMonth
}

function randomNumber(max: number): number {
  return Math.floor(Math.random() * max) + 1 // no zero
}

function ageInYears(dateOfBirth: Date, today: Date): number {
  return today.getFullYear() - dateOfBirth.getFullYear() - (birthDayPassed(dateOfBirth, today) ? 0 : 1)
}

function birthDayPassed(dateOfBirth: Date, today: Date): boolean {
  return (
    dateOfBirth.getMonth() < today.getMonth() ||
    (dateOfBirth.getMonth() === today.getMonth() && dateOfBirth.getDate() <= today.getDate())
  )
}

function getCurrentDate(date: Date): string {
  return `0${date.getDate()}`.slice(-2)
}

function getCurrentMonth(date: Date): string {
  return `0${date.getMonth() + 1}`.slice(-2)
}

export const getBirthdateFromSsn = (ssn: string): string => {
  const parsedSsn = FinnishSSN.parse(ssn)
  if (!parsedSsn.valid) {
    throw new Error(`Non-finnish SSN: '${ssn}'`)
  }
  const date = parsedSsn.dateOfBirth.getDate()
  const month = parsedSsn.dateOfBirth.getMonth() + 1 // zero based months
  const year = parsedSsn.dateOfBirth.getFullYear()
  const zeroPaddedDate = String(date).padStart(2, '0')
  const zeroPaddedMonth = String(month).padStart(2, '0')
  return `${year}-${zeroPaddedMonth}-${zeroPaddedDate}`
}
